import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectDetailView } from './components/ProjectDetailView';
import { DashboardProject } from '../project/types/project';
import { fetchNDTAnalytics } from '../../redux/NDT Analytics/actions';
import { fetchNDTCategories } from '../../redux/ProductionData/actions';
import { getAllPipeDetails } from '../../redux/Project/setupData/action';
import type { CategoryFieldConfig, PipeOption } from './components/FilterPanel';

interface NDTDashboardProps {
  project: DashboardProject;
}

export const NDTDashboard: React.FC<NDTDashboardProps> = ({ project }) => {
  const dispatch = useDispatch();
  const { data: reduxData, filters: reduxFilters, isFetching, error } = useSelector(
    (state: any) => state.ndtAnalytics || {}
  );
  const ndtCategoryData = useSelector(
    (state: any) => state.productionData?.ndtCategoryData ?? null
  );
  const { pipeDetails } = useSelector((state: any) => state.setupData || {});

  const pipeOptions: PipeOption[] = useMemo(() => {
    if (!pipeDetails || typeof pipeDetails !== 'object') return [];
    const list: PipeOption[] = [];
    Object.values(pipeDetails).forEach((setup: any) => {
      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          const displayName =
            setup.projectSubType === 'none' && setup.projectType !== 'fabrication'
              ? `${setup.projectType === 'pipeline' ? 'Pipeline' : setup.projectType === 'others' ? 'Others' : setup.projectType} ${pipe.pipeName || 'Pipe'} (${pipe.pipeSize ?? ''} x ${pipe.wallThickness ?? ''})`
              : `${setup.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' : setup.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' : setup.projectSubType === 'bulkhead' ? 'Bulk Head' : setup.projectSubType || 'Pipe'} ${pipe.pipeName || 'Pipe'} (${pipe.pipeSize ?? ''} x ${pipe.wallThickness ?? ''})`;
          list.push({ pipeId: pipe.pipeId, displayName });
        });
      }
    });
    return list;
  }, [pipeDetails]);

  const [selectedFilters, setSelectedFilters] = useState<any | null>(null);
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const lastFetchedProjectIdRef = useRef<string | number | null>(null);

  const toPipeIdApi = (pipeId: string[] | string | null | undefined): number | number[] | null => {
    if (pipeId == null) return null;
    const ids = (Array.isArray(pipeId) ? pipeId : [pipeId])
      .filter((id) => id != null && id !== '')
      .map(Number)
      .filter((id) => !Number.isNaN(id));
    if (ids.length === 0) return null;
    if (ids.length === 1) return ids[0];
    return ids;
  };

  // Fetch data only once on mount or when projectId actually changes
  useEffect(() => {
    const projectId = project?.project?.projectId || null;
    
    // Only fetch if projectId has changed or hasn't been fetched yet
    if (projectId !== null && projectId !== lastFetchedProjectIdRef.current) {
      lastFetchedProjectIdRef.current = projectId;
      dispatch(getAllPipeDetails({ projectId }));
      dispatch(fetchNDTCategories({
        projectId: Number(projectId),
        columnNames: ['wps_number', 'wall_thickness', 'location']
      }));
      const payload = {
        project: projectId,
        pipe_id: null,
        sub_project: null,
        pipe_diameter: null,
        wall_thickness: null,
        shift: null,
        location: null,
        critical_weld: null,
        wps_number: null,
        welder_number: null,
        welder_repair_number: null,
        evaluation: null,
        defect_type: null,
        start_date: null,
        end_date: null,
        limit: null
      };
      dispatch(fetchNDTAnalytics(payload));
     
    }
  }, [dispatch, project?.project?.projectId]);

  // Use Redux data only
  const data = reduxData;
  const currentFilters = reduxFilters || (data?.data?.metadata?.filters || data?.metadata?.filters || null);
  
  // Check if response indicates no records found
  const isNoRecordsFound = (typeof error === 'string' && error.includes('No records found')) || 
                           (data?.detail && data.detail === 'No records found for the provided filters.');

  const toFilterArray = (v: string[] | string | null | undefined): string[] | null => {
    if (v == null) return null;
    if (Array.isArray(v)) return v.length > 0 ? v : null;
    return v !== '' ? [String(v)] : null;
  };

  const handleFilterChange = (filters: {
    project?: string | null;
    subProject?: string[] | string | null;
    pipeId?: string[] | string | null;
    pipeDiameter?: string | null;
    wallThickness?: string[] | string | null;
    shift?: string | null;
    location?: string[] | string | null;
    criticalWeld?: string | null;
    wpsNumber?: string[] | string | null;
  }) => {
    setSelectedFilters(filters);

    const projectId = project?.project?.projectId || null;
    const payload = {
      project: projectId,
      pipe_id: toPipeIdApi(filters.pipeId),
      sub_project: toFilterArray(filters.wpsNumber || filters.subProject),
      pipe_diameter: filters.pipeDiameter || null,
      wall_thickness: toFilterArray(filters.wallThickness),
      shift: filters.shift || null,
      location: toFilterArray(filters.location),
      critical_weld: filters.criticalWeld || null,
      wps_number: toFilterArray(filters.wpsNumber),
      welder_number: null,
      welder_repair_number: null,
      evaluation: null,
      defect_type: null,
      start_date: null,
      end_date: null,
      limit: null
    };

    dispatch(fetchNDTAnalytics(payload));
  };

  const ndtCategoryFields: CategoryFieldConfig[] = [
    { key: 'wpsNumber', label: 'WPS Number', categoryKey: 'wps_number' },
    { key: 'wallThickness', label: 'Wall thickness', categoryKey: 'wall_thickness' },
    { key: 'location', label: 'Location', categoryKey: 'location' }
  ];

  const handleBackToProjects = () => {
    // Handle back navigation if needed
    console.log('Back to projects');
  };

  const displayData = reduxData?.data || reduxData;

  return (
    <div>
      <ProjectDetailView
        data={displayData ?? null}
        selectedProject={'NDT Analytics'}
        filters={currentFilters}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onBack={handleBackToProjects}
        isLoading={isFetching}
        error={error ?? null}
        isNoRecordsFound={isNoRecordsFound}
        currentFile={currentFile}
        projectId={project?.project?.projectId || null}
        categoryData={ndtCategoryData}
        categoryFields={ndtCategoryFields}
        pipeOptions={pipeOptions}
        onDataUpdate={(newData: any) => {
          console.log('Data updated:', newData);
        }}
      />
    </div>
  );
};

export default NDTDashboard;